import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { loadMarkdownBySlug } from '../utils/markdownLoader.js';
import { getArticleImage } from '../data/articleImages.js';
import Seo from '../components/Seo.jsx';
import { resolveAuthor } from '../data/contributors.js';
import { articleSchema, howToSchema, breadcrumbSchema } from '../utils/schema.js';

// Lazy glob — each article becomes its own async chunk so we only fetch
// the one the visitor asked for. See utils/markdownLoader.js.
//
// Session 41 — glob points at ../articles/enriched/*.md rather than
// ../articles/*.md. Source-of-truth lives in src/articles/ (hand-edited);
// the enriched copies are generated by scripts/inject-article-city-links.mjs
// as a prebuild step and carry inline markdown links to dedicated city
// pages. Mirrors the v39 state-article enrichment pattern
// (../states/enriched/*.md) and the v41 city-data enrichment
// (../data/cities/enriched/*.js).
const articleModules = import.meta.glob('../articles/enriched/*.md', {
  query: '?raw',
  import: 'default',
});

// Parse frontmatter from raw markdown
function parseFrontmatter(raw) {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/);
  if (!match) return { frontmatter: {}, body: raw };

  const yamlText = match[1];
  const body = match[2];
  const frontmatter = {};

  // Simple YAML parser for our known schema
  const lines = yamlText.split('\n');
  let currentKey = null;
  let currentObj = null;
  let currentArray = null;
  let inMultiline = false;
  let currentSubArrayKey = null;  // tracks `tldr.items`-style nested arrays

  for (const line of lines) {
    if (line.trim() === '' || line.trim().startsWith('#')) continue;

    // Top-level key
    const topMatch = line.match(/^([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (topMatch && !line.startsWith(' ')) {
      currentKey = topMatch[1];
      currentSubArrayKey = null;
      const value = topMatch[2].trim();

      if (value === '') {
        // Object or array follows
        frontmatter[currentKey] = null;
        currentObj = null;
        currentArray = null;
      } else if (value.startsWith('"') && value.endsWith('"')) {
        frontmatter[currentKey] = value.slice(1, -1);
      } else {
        frontmatter[currentKey] = value;
      }
      continue;
    }

    // Indented sub-key (object) — 2 spaces
    const subMatch = line.match(/^  ([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (subMatch) {
      const subKey = subMatch[1];
      const subValue = subMatch[2].trim();
      if (frontmatter[currentKey] === null || typeof frontmatter[currentKey] !== 'object') {
        frontmatter[currentKey] = {};
      }
      if (subValue === '') {
        // Empty value — array of items will follow at 4-space indent
        frontmatter[currentKey][subKey] = [];
        currentSubArrayKey = subKey;
      } else if (subValue.startsWith('"') && subValue.endsWith('"')) {
        frontmatter[currentKey][subKey] = subValue.slice(1, -1);
        currentSubArrayKey = null;
      } else {
        frontmatter[currentKey][subKey] = subValue;
        currentSubArrayKey = null;
      }
      continue;
    }

    // 4-space-indent array item under a sub-key — `    - "value"`
    const subArrayMatch = line.match(/^    - (.*)$/);
    if (subArrayMatch && currentSubArrayKey && Array.isArray(frontmatter[currentKey]?.[currentSubArrayKey])) {
      const item = subArrayMatch[1].trim();
      if (item.startsWith('"') && item.endsWith('"')) {
        frontmatter[currentKey][currentSubArrayKey].push(item.slice(1, -1));
      } else {
        frontmatter[currentKey][currentSubArrayKey].push(item);
      }
      continue;
    }

    // Array item — `  - "value"` or `  - key: value`
    const arrayMatch = line.match(/^  - (.*)$/);
    if (arrayMatch) {
      currentSubArrayKey = null;
      const item = arrayMatch[1].trim();
      if (!Array.isArray(frontmatter[currentKey])) {
        frontmatter[currentKey] = [];
      }
      // Object item (starts with key:)
      const keyMatch = item.match(/^([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
      if (keyMatch) {
        const obj = {};
        const k = keyMatch[1];
        const v = keyMatch[2].trim();
        obj[k] = v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1) : v;
        frontmatter[currentKey].push(obj);
      } else if (item.startsWith('"') && item.endsWith('"')) {
        frontmatter[currentKey].push(item.slice(1, -1));
      } else {
        frontmatter[currentKey].push(item);
      }
      continue;
    }

    // Sub-array item under an object — `    key: value`
    const subSubMatch = line.match(/^    ([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (subSubMatch && Array.isArray(frontmatter[currentKey]) && frontmatter[currentKey].length > 0) {
      const lastItem = frontmatter[currentKey][frontmatter[currentKey].length - 1];
      if (typeof lastItem === 'object') {
        const k = subSubMatch[1];
        const v = subSubMatch[2].trim();
        lastItem[k] = v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1) : v;
      }
    }
  }

  return { frontmatter, body };
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [dark, setDark] = useState(false);
  const [content, setContent] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadMarkdownBySlug(articleModules, slug, parseFrontmatter).then((found) => {
      if (cancelled) return;
      setContent(found);
      setLoading(false);
      window.scrollTo(0, 0);
    });
    return () => { cancelled = true; };
  }, [slug]);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  if (loading) {
    // Brief loading state during slug transitions. Same color palette
    // as the article shell, so layout doesn't jump.
    return (
      <div style={{
        minHeight: '100vh', background: T.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: fontMono, fontSize: 11, letterSpacing: '0.22em',
        color: T.inkFaint, padding: 40,
      }}>
        ONE MOMENT
      </div>
    );
  }

  if (!content) {
    return (
      <div style={{
        minHeight: '100vh', background: T.bg, color: T.ink,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: fontDisplay, fontSize: 24, fontStyle: 'italic',
        padding: 40,
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: T.inkFaint, marginBottom: 24 }}>Article not found.</p>
          <Link to="/" style={{
            color: T.sienna, textDecoration: 'none',
            fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em',
            fontStyle: 'normal',
          }}>
            ← BACK TO HOMEPAGE
          </Link>
        </div>
      </div>
    );
  }

  const { frontmatter: fm, body } = content;
  // s80: byline display fields come from contributors.js (one source).
  const author = resolveAuthor(fm.author);
  const heroImage = getArticleImage(fm.slug);

  // Format dates nicely
  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, transition: 'background 0.3s, color 0.3s',
    }}>
      <Seo
        title={fm.metaTitle || fm.title}
        description={fm.metaDescription || fm.subtitle}
        path={`/articles/${fm.slug}`}
        ogType="article"
        ogImage={heroImage ? `https://windowwashingguide.com${heroImage.hero}` : undefined}
        publishedTime={fm.publishedDate}
        modifiedTime={fm.updatedDate}
        jsonLd={[
          articleSchema(
            { ...fm, author, image: heroImage ? `https://windowwashingguide.com${heroImage.hero}` : undefined },
            author?.slug,
          ),
          howToSchema(fm),
          breadcrumbSchema([
            { name: 'Guide', path: '/' },
            { name: fm.pillarLabel ? fm.pillarLabel[0] + fm.pillarLabel.slice(1).toLowerCase() : 'Articles', path: fm.pillar ? `/${fm.pillar}` : '/articles' },
            { name: fm.title, path: `/articles/${fm.slug}` },
          ]),
        ]}
      />
      <style>{`
        .article-prose p {
          font-family: ${fontDisplay};
          font-size: 18px;
          line-height: 1.7;
          color: ${T.ink};
          margin: 0 0 24px;
          font-weight: 400;
        }
        .article-prose h2 {
          font-family: ${fontDisplay};
          font-size: 32px;
          font-weight: 400;
          line-height: 1.15;
          letter-spacing: -0.02em;
          margin: 56px 0 24px;
          color: ${T.ink};
        }
        .article-prose h3 {
          font-family: ${fontDisplay};
          font-size: 22px;
          font-weight: 500;
          line-height: 1.2;
          margin: 36px 0 16px;
          color: ${T.ink};
        }
        .article-prose blockquote {
          border-left: 2px solid ${T.sienna};
          padding: 4px 0 4px 28px;
          margin: 32px 0;
          font-family: ${fontDisplay};
          font-style: italic;
          font-size: 22px;
          line-height: 1.45;
          color: ${T.ink};
          font-weight: 400;
        }
        .article-prose blockquote em {
          color: ${T.sienna};
        }
        .article-prose strong {
          font-weight: 600;
          color: ${T.ink};
        }
        .article-prose em {
          font-style: italic;
          color: ${T.sienna};
        }
        .article-prose ul, .article-prose ol {
          font-family: ${fontDisplay};
          font-size: 17px;
          line-height: 1.65;
          color: ${T.ink};
          padding-left: 28px;
          margin: 0 0 24px;
        }
        .article-prose li { margin-bottom: 8px; }
        .article-prose a {
          color: ${T.sienna};
          text-decoration: underline;
          text-decoration-thickness: 1px;
          text-underline-offset: 3px;
        }
        .article-prose hr {
          border: none;
          border-top: 1px solid ${T.rule};
          margin: 56px 0;
        }
        .article-prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 32px 0;
          font-family: ${fontBody};
          font-size: 14px;
        }
        .article-prose th {
          font-family: ${fontMono};
          font-size: 10px;
          letter-spacing: 0.15em;
          color: ${T.inkFaint};
          text-align: left;
          padding: 12px 16px 12px 0;
          border-bottom: 1px solid ${T.ink};
          font-weight: 500;
        }
        .article-prose td {
          padding: 14px 16px 14px 0;
          border-bottom: 1px solid ${T.rule};
          color: ${T.ink};
          line-height: 1.5;
        }
        .article-prose code {
          font-family: ${fontMono};
          font-size: 13px;
          background: ${T.surface};
          padding: 1px 6px;
          border-radius: 2px;
        }
        .article-prose sup a {
          font-size: 11px;
          color: ${T.sienna};
          text-decoration: none;
          padding: 0 2px;
        }
      `}</style>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px 80px' }}>
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em',
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Breadcrumb */}
        {fm.breadcrumb && (
          <div style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.15em', color: T.inkFaint,
            marginBottom: 36,
          }}>
            {fm.breadcrumb}
          </div>
        )}

        {/* Hero image — curated topical photography, full column width.
            Rendered as a <figure> with a thin photographer credit beneath,
            in keeping with the editorial type system. Lazy-decoded; fixed
            aspect ratio so the layout doesn't shift while it loads. */}
        {heroImage && (
          <figure style={{ margin: '0 0 40px' }}>
            <div style={{
              width: '100%', aspectRatio: '3 / 2',
              background: T.surface, overflow: 'hidden',
              border: `1px solid ${T.rule}`,
            }}>
              <img
                src={heroImage.hero}
                alt={heroImage.alt}
                width={1200}
                height={800}
                loading="eager"
                decoding="async"
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover', display: 'block',
                }}
              />
            </div>
            {heroImage.credit && (
              <figcaption style={{
                fontFamily: fontMono, fontSize: 10,
                letterSpacing: '0.12em', color: T.inkFaint,
                marginTop: 8, textAlign: 'right',
              }}>
                PHOTO ·{' '}
                <a
                  href={heroImage.creditUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  style={{ color: T.inkFaint, textDecoration: 'none' }}
                >
                  {heroImage.credit.toUpperCase()}
                </a>
                {' / '}
                <a
                  href={heroImage.photoUrl || (heroImage.source === 'Pexels'
                    ? 'https://www.pexels.com'
                    : 'https://pixabay.com')}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  style={{ color: T.inkFaint, textDecoration: 'none' }}
                >
                  {(heroImage.source || '').toUpperCase()}
                </a>
              </figcaption>
            )}
          </figure>
        )}

        {/* Pillar tag + read time */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 20,
        }}>
          <span style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
          }}>
            {fm.pillarLabel} &nbsp;&nbsp;&nbsp; № {fm.articleNumber}
          </span>
          <span style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.15em', color: T.inkFaint,
          }}>
            {fm.readTime} {fm.wordCount && `· ${fm.wordCount} WORDS`}
          </span>
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: fontDisplay, fontSize: 48, fontWeight: 400,
          lineHeight: 1.05, letterSpacing: '-0.025em',
          margin: '0 0 24px',
        }}>
          {fm.title}
        </h1>

        {/* Subtitle */}
        {fm.subtitle && (
          <p style={{
            fontFamily: fontDisplay, fontSize: 20, fontWeight: 300,
            lineHeight: 1.5, color: T.inkSoft,
            margin: '0 0 32px',
          }}>
            {fm.subtitle}
          </p>
        )}

        {/* Author */}
        {author && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            paddingTop: 20, paddingBottom: 20,
            borderTop: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}`,
            marginBottom: 40,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: T.surface, border: `1px solid ${T.rule}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: fontDisplay, fontSize: 20, color: T.inkSoft,
            }}>
              {author.initial || author.name?.[0]}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: fontBody, fontWeight: 500, fontSize: 14 }}>
                {author.name}
              </div>
              <div style={{
                fontFamily: fontMono, fontSize: 10,
                letterSpacing: '0.12em', color: T.inkFaint,
              }}>
                {author.role}
              </div>
            </div>
            <div style={{
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.12em', color: T.inkFaint,
              textAlign: 'right',
            }}>
              {fm.updatedDate && <>UPDATED {formatDate(fm.updatedDate).toUpperCase()}<br /></>}
              {fm.publishedDate && <>PUB. {formatDate(fm.publishedDate).toUpperCase()}</>}
            </div>
          </div>
        )}

        {/* TL;DR card */}
        {fm.tldr && (
          <div style={{
            background: T.surface,
            borderLeft: `2px solid ${T.sienna}`,
            padding: '24px 28px', marginBottom: 40,
          }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 12,
            }}>
              ⚡ THE SHORT ANSWER
            </div>
            {fm.tldr.headline && (
              <p style={{
                fontFamily: fontDisplay, fontSize: 19, fontWeight: 400,
                lineHeight: 1.4, margin: '0 0 16px', color: T.ink,
              }}>
                {fm.tldr.headline}
              </p>
            )}
            {fm.tldr.items && (
              <ul style={{
                fontFamily: fontBody, fontSize: 15, color: T.ink,
                lineHeight: 1.55, paddingLeft: 18, margin: '0 0 12px',
              }}>
                {fm.tldr.items.map((item, i) => (
                  <li key={i} style={{ marginBottom: 8 }}>
                    <ReactMarkdown
                      components={{ p: ({ children }) => <>{children}</> }}
                    >{item}</ReactMarkdown>
                  </li>
                ))}
              </ul>
            )}
            {fm.tldr.closing && (
              <p style={{
                fontFamily: fontBody, fontSize: 14,
                color: T.inkSoft, lineHeight: 1.5, margin: 0,
              }}>
                {/* Markdown, exactly like tldr.items above. Until s84 this was
                    `{fm.tldr.closing}` — raw JSX text — so six articles shipped
                    literal `[anchor](/articles/slug)` source into the TL;DR box
                    and nine intended internal links rendered as zero anchors.
                    Confirmed in a real DOM, not inferred. The items were parsed
                    and the closing line beside them was not, which is why no
                    gate and no reader ever flagged it: the raw text still reads
                    as a sentence. The `p` unwrap keeps this inside the styled
                    <p> instead of nesting <p> in <p>.
                    Guarded by scripts/probe-raw-markdown.mjs. */}
                <ReactMarkdown
                  components={{ p: ({ children }) => <>{children}</> }}
                >{fm.tldr.closing}</ReactMarkdown>
              </p>
            )}
          </div>
        )}

        {/* Body */}
        <div className="article-prose">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
        </div>

        {/* Author bio at end */}
        {author?.bio && (
          <div style={{
            marginTop: 56, paddingTop: 32,
            borderTop: `1px solid ${T.rule}`,
          }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 12,
            }}>
              ABOUT THE AUTHOR
            </div>
            <h3 style={{
              fontFamily: fontDisplay, fontSize: 26, fontWeight: 400,
              margin: '0 0 12px',
            }}>
              {author.name}
            </h3>
            <p style={{
              fontFamily: fontBody, fontSize: 14, lineHeight: 1.6,
              color: T.inkSoft, margin: 0,
            }}>
              {author.bio}
            </p>
            {author.slug && (
              <p style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: '0.14em', margin: '14px 0 0' }}>
                <Link to={`/contributors/${author.slug}`} style={{ color: T.sienna, textDecoration: 'none' }}>
                  ALL WORK BY {author.name.split(' ')[0].toUpperCase()} →
                </Link>
                <span style={{ color: T.inkFaint }}> · </span>
                <Link to="/editorial-standards" style={{ color: T.sienna, textDecoration: 'none' }}>EDITORIAL STANDARDS →</Link>
              </p>
            )}
          </div>
        )}

        {/* Related — the curated `related:` block, rendered since s83.
            Titles and pillar labels in that frontmatter are DERIVED, not
            authored: sync-related-titles.mjs rewrites them from each target
            article's own frontmatter on every build, and
            verify-related-blocks.mjs fails the build if any of them drift or
            point at a slug that does not exist. So this renders stored strings
            without the stale-copy risk that kept the block unrendered for
            three sessions. The authored part is the slug list — the curation. */}
        {Array.isArray(fm.related) && fm.related.length > 0 && (
          <div style={{
            marginTop: 56, paddingTop: 32,
            borderTop: `1px solid ${T.rule}`,
          }}>
            <div style={{
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 20,
            }}>
              RELATED READING
            </div>
            <div style={{
              display: 'grid', gap: 20,
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            }}>
              {fm.related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/articles/${r.slug}`}
                  style={{
                    textDecoration: 'none', color: T.ink,
                    background: T.surface,
                    border: `1px solid ${T.rule}`,
                    padding: '18px 18px 20px',
                    display: 'block',
                  }}
                >
                  {r.pillar && (
                    <div style={{
                      fontFamily: fontMono, fontSize: 9,
                      letterSpacing: '0.18em', color: T.inkFaint,
                      marginBottom: 10,
                    }}>
                      {r.pillar}
                    </div>
                  )}
                  <div style={{
                    fontFamily: fontDisplay, fontSize: 17,
                    lineHeight: 1.32,
                  }}>
                    {r.title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Prev/Next nav */}
        {(fm.previous || fm.next) && (
          <div style={{
            marginTop: 56, paddingTop: 32,
            borderTop: `1px solid ${T.rule}`,
            display: 'grid', gap: 24,
            gridTemplateColumns: fm.previous && fm.next ? '1fr 1fr' : '1fr',
          }}>
            {fm.previous && (
              <Link to={`/articles/${fm.previous.slug}`} style={{
                textDecoration: 'none', color: T.ink,
              }}>
                <div style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 8,
                }}>
                  ← PREVIOUS
                </div>
                <div style={{ fontFamily: fontDisplay, fontSize: 18, lineHeight: 1.3 }}>
                  {fm.previous.title}
                </div>
              </Link>
            )}
            {fm.next && (
              <Link to={`/articles/${fm.next.slug}`} style={{
                textDecoration: 'none', color: T.ink,
                textAlign: fm.previous ? 'right' : 'left',
              }}>
                <div style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 8,
                }}>
                  NEXT →
                </div>
                <div style={{ fontFamily: fontDisplay, fontSize: 18, lineHeight: 1.3 }}>
                  {fm.next.title}
                </div>
              </Link>
            )}
          </div>
        )}

        {/* Footer */}
        <footer style={{
          marginTop: 56, paddingTop: 24,
          borderTop: `1px solid ${T.rule}`,
        }}>
          <Link to="/" style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            textDecoration: 'none',
          }}>
            ← BACK TO WINDOW WASHING GUIDE
          </Link>
        </footer>
      </div>
    </div>
  );
}
